/**
 * The T-permit guide library — the single list of which blog posts belong to
 * the /t-permit hub.
 *
 * Read by pages/buyer/TPermitHub.js (the hub's sections), pages/buyer/BlogPost.js
 * (the "Part of the T-Permit Guide" link and breadcrumb on each member post) and
 * scripts/inject-seo-heads.js (the same breadcrumb in the static head). Until
 * 2026-09-28 the list lived only inside the hub, so the hub linked down to 21
 * guides and not one guide linked back up.
 *
 * Import-free on purpose: the injector loads it by stripping `export` and
 * requiring it.
 */
// The permit library, grouped by what the reader is actually trying to do.
// Every slug here is a live post in src/data/blogPosts.js.
//
// Three permit-cluster posts are deliberately NOT linked from this hub:
//   • t-permit-car-loan-finance-india — finance content, and site policy is no
//     loan/EMI material on used-vehicle journeys.
//   • yellow-number-plate-cars-for-sale-india
//   • taxi-with-permit-for-sale-india
//     Both are commercial "for sale" intent, not regulatory explainers. They
//     belong to /vehicles, which the commercial section below points at.
export const CLUSTERS = [
  {
    id: 'basics',
    heading: 'Start here — what a T-permit actually is',
    blurb: 'The vocabulary is confusing because three different things get called the same thing: the registration series, the plate colour, and the permit itself.',
    posts: [
      ['what-is-t-permit-car-buying-guide-india', 'What Is a T-Permit Car? Complete Buying Guide'],
      ['commercial-vehicle-permit-types-india', 'Every Commercial Vehicle Permit Type, Explained'],
      ['t-permit-vs-private-car-buyers-guide', 'T-Permit vs Private Car: Which Should You Buy?'],
    ],
  },
  {
    id: 'getting',
    heading: 'Getting a permit',
    blurb: 'What it costs, what paperwork the RTO wants, and the licence the driver needs alongside it.',
    posts: [
      ['t-permit-cost-renewal-validity-india', 'T-Permit Cost, Validity & Renewal'],
      ['t-permit-car-documents-checklist-india', 'T-Permit Documents Checklist Before You Buy'],
      ['commercial-car-registration-process-india', 'Commercial Registration & Yellow Plate Process'],
      ['yellow-plate-taxi-registration-delhi', 'Registering a Yellow Plate Taxi in Delhi'],
      ['psv-badge-commercial-licence-india', 'PSV Badge & Commercial Driving Licence'],
    ],
  },
  {
    id: 'converting',
    heading: 'Converting between private and commercial',
    blurb: 'Both directions are possible. Neither is quick, and the cost is usually underestimated.',
    posts: [
      ['convert-private-car-to-t-permit-commercial', 'Converting a Private Car to a T-Permit Commercial Car'],
      ['convert-t-permit-to-private-car-india', 'Converting a T-Permit Car Back to White Plate'],
    ],
  },
  {
    id: 'interstate',
    heading: 'Running across state lines',
    blurb: 'A state permit stops at the state border. If you take outstation or airport work, this is the one to read.',
    posts: [
      ['aitp-all-india-tourist-permit-delhi', 'All India Tourist Permit (AITP): Cost, Documents & Process'],
      ['delhi-taxi-permit-2026', 'Delhi Taxi Permit Rules: Tourist, Contract & Radio Cab'],
    ],
  },
  {
    id: 'compliance',
    heading: 'Tax, fitness and staying legal',
    blurb: 'The recurring obligations that catch out first-time commercial owners.',
    posts: [
      ['taxi-gst-tax-rules-india', 'GST & Tax Rules for Taxi Owners'],
      ['gst-on-buying-commercial-vehicle-india', 'GST When Buying a Commercial Vehicle (ITC Rules)'],
      ['commercial-vehicle-fitness-delhi', 'Fitness Certificate for Commercial Vehicles'],
      ['delhi-taxi-scrappage-policy-2026', 'Delhi Scrappage Policy & Age Limits'],
    ],
  },
  {
    id: 'buying',
    heading: 'Buying, verifying and reselling a permit vehicle',
    blurb: 'A permit is part of the asset you are buying. These cover how to check it and what it is worth.',
    posts: [
      ['best-t-permit-cars-india-2026', 'Best T-Permit Cars to Buy in India'],
      ['rc-document-check-before-buying-taxi', 'Verifying RC & Documents Before You Buy'],
      ['delhi-rc-transfer-guide', 'Transferring the RC of a Taxi in Delhi'],
      ['t-permit-car-resale-value-guide-india', 'T-Permit Resale Value: Buyer & Seller Guide'],
      ['ola-uber-registration-delhi', 'Registering on Ola & Uber in Delhi'],
    ],
  },
];

export const TPERMIT_HUB = { name: 'T-Permit Guide', path: '/t-permit' };

export function isTPermitGuide(slug) {
  return CLUSTERS.some((c) => c.posts.some(([s]) => s === slug));
}
